import React, { useState, useEffect } from 'react';
import { useLMS } from '../../context/LMSContext';
import { storage, supabase } from '../../lib/supabase';

const SyllabusView = () => {
  const { 
    currentUser,
    selectedCohortId,
    getCohortById,
    getModulesForCohort,
    addSyllabusPdf,
    deleteSyllabusPdf,
    setCurrentView,
    setSelectedWeek
  } = useLMS();

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [newPdf, setNewPdf] = useState({
    title: '',
    filename: ''
  });

  // =========================================================================
  // MASTER PDFs STATE (cohort_id IS NULL)
  // =========================================================================
  const [masterPdfs, setMasterPdfs] = useState([]);
  const [isLoadingMasterPdfs, setIsLoadingMasterPdfs] = useState(true);

  const isAdmin = currentUser?.role === 'admin';
  const isStaff = isAdmin || currentUser?.role === 'professor';
  const cohort = getCohortById(selectedCohortId);
  const modules = getModulesForCohort(selectedCohortId) || [];

  // =========================================================================
  // LOAD MASTER PDFs (visible to ALL cohorts)
  // =========================================================================
  useEffect(() => {
    const loadMasterPdfs = async () => {
      setIsLoadingMasterPdfs(true);
      try {
        const { data, error } = await supabase
          .from('syllabus_pdfs')
          .select('*')
          .is('cohort_id', null)
          .order('uploaded_at', { ascending: false });

        if (error) throw error;
        setMasterPdfs(data || []);
      } catch (err) {
        console.error('Error loading master PDFs:', err);
      } finally {
        setIsLoadingMasterPdfs(false);
      }
    };

    loadMasterPdfs();
  }, []);

  if (!cohort) {
    return (
      <div className="syllabus-view">
        <div className="empty-state">
          <h2>No cohort selected</h2>
          <p>Please select a cohort from the sidebar</p>
        </div>
      </div>
    );
  }

  // Build course summary from all assignments/quizzes/exams (with safety check)
  const courseSummary = modules.flatMap(module => 
    (module.items || [])
      .filter(item => ['assignment', 'quiz', 'exam'].includes(item.type))
      .map(item => ({
        ...item,
        weekNumber: module.weekNumber
      }))
  );

  // Cohort-specific PDFs
  const cohortPdfs = cohort.syllabusPdfs || [];

  // Combined: master PDFs first, then cohort-specific
  const allDocuments = [
    ...masterPdfs.map(pdf => ({ ...pdf, isMaster: true })),
    ...cohortPdfs.map(pdf => ({ ...pdf, isMaster: false }))
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatFileSize = (size) => {
    if (!size) return '';
    // Handle string sizes like "1.2 MB"
    if (typeof size === 'string') return size;
    // Handle numeric sizes
    const mb = size / (1024 * 1024);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    const kb = size / 1024;
    return `${kb.toFixed(0)} KB`;
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setUploadError('');
    
    if (!selectedFile) {
      setUploadError('Please select a PDF file');
      return;
    }
    
    setUploading(true);
    
    try {
      console.log('Starting upload for:', selectedFile.name);
      
      // Upload to Supabase Storage
      const uploadResult = await storage.uploadPdf(selectedCohortId, selectedFile);
      
      console.log('Upload result:', uploadResult);
      
      if (uploadResult.error) {
        throw new Error(uploadResult.error.message || 'Upload failed');
      }
      
      if (!uploadResult.data) {
        throw new Error('No data returned from upload');
      }
      
      // Save metadata to database via context
      addSyllabusPdf(selectedCohortId, {
        title: newPdf.title,
        filename: uploadResult.data.filename,
        file_url: uploadResult.data.url,
        file_path: uploadResult.data.path,
        fileSize: uploadResult.data.size
      });
      
      setShowUploadModal(false);
      setNewPdf({ title: '', filename: '' });
      setSelectedFile(null);
    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message || 'Failed to upload PDF. Check storage bucket permissions.');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setUploadError('Please select a PDF file');
        return;
      }
      setSelectedFile(file);
      setNewPdf({...newPdf, filename: file.name});
      setUploadError('');
    }
  };

  const handleDelete = async (pdf) => {
    // Prevent deleting master PDFs from this view
    if (pdf.isMaster) {
      alert('Master documents can only be deleted from the Master Modules section.');
      return;
    }

    if (window.confirm('Delete this document?')) {
      // Delete from storage if file_path exists
      if (pdf.file_path) {
        await storage.deletePdf(pdf.file_path);
      }
      deleteSyllabusPdf(selectedCohortId, pdf.id);
    }
  };

  const handleAssignmentClick = (weekNumber) => {
    setSelectedWeek(weekNumber);
    setCurrentView('module');
  };

  const getTypeIcon = (type) => {
    const icons = { assignment: '📝', quiz: '❓', exam: '📋' };
    return icons[type] || '📄';
  };

  return (
    <div className="syllabus-view">
      {/* Header - UPDATED TITLE */}
      <div className="page-header">
        <div className="breadcrumb">
          <span onClick={() => setCurrentView('dashboard')}>Dashboard</span>
          <span className="separator">/</span>
          <span onClick={() => setCurrentView('cohort')}>{cohort.name}</span>
          <span className="separator">/</span>
          <span className="current">Syllabus & Attachments</span>
        </div>
        
        <h1>📋 Syllabus & Attachments</h1>
        <p>{cohort.name}</p>
      </div>

      {/* Course Info */}
      <div className="info-card">
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Duration</span>
            <span className="info-value">{formatDate(cohort.startDate)} - {formatDate(cohort.endDate)}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Live Lectures</span>
            <span className="info-value">Mon, Wed, Fri</span>
          </div>
          <div className="info-item">
            <span className="info-label">Lecture Time</span>
            <span className="info-value">11:30 AM - 3:00 PM EST</span>
          </div>
          <div className="info-item">
            <span className="info-label">Total Weeks</span>
            <span className="info-value">6 weeks</span>
          </div>
        </div>
      </div>

      {/* PDF Documents - UPDATED SECTION */}
      <div className="syllabus-section">
        <div className="section-header">
          <h2>📄 Course Documents</h2>
          {isStaff && (
            <button className="btn-primary" onClick={() => setShowUploadModal(true)}>
              + Upload PDF
            </button>
          )}
        </div>

        {/* Loading state */}
        {isLoadingMasterPdfs ? (
          <div className="documents-grid">
            <div className="empty-state">
              <p>Loading documents...</p>
            </div>
          </div>
        ) : (
          <div className="documents-grid">
            {allDocuments.length > 0 ? (
              allDocuments.map(pdf => (
                <div key={pdf.id} className={`document-card ${pdf.isMaster ? 'master-document' : ''}`}>
                  <div className="document-icon">
                    {pdf.isMaster ? '📎' : '📄'}
                  </div>
                  <div className="document-info">
                    <h4>
                      {pdf.title}
                      {pdf.isMaster && (
                        <span className="master-badge" title="Available to all cohorts">
                          All Cohorts
                        </span>
                      )}
                    </h4>
                    <p>
                      {pdf.filename} 
                      {(pdf.fileSize || pdf.file_size) && ` • ${formatFileSize(pdf.fileSize || pdf.file_size)}`}
                    </p>
                    <span className="upload-date">
                      Uploaded {formatDate(pdf.uploadedAt || pdf.uploaded_at)}
                    </span>
                  </div>
                  <div className="document-actions">
                    {pdf.file_url ? (
                      <>
                        <a 
                          href={pdf.file_url} 
                          download={pdf.filename}
                          className="btn-secondary small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          📥 Download
                        </a>
                        <a 
                          href={pdf.file_url} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="btn-secondary small"
                          onClick={(e) => e.stopPropagation()}
                        >
                          👁️ View
                        </a>
                      </>
                    ) : (
                      <>
                        <button className="btn-secondary small" disabled>📥 Download</button>
                        <button className="btn-secondary small" disabled>👁️ View</button>
                      </>
                    )}
                    {/* Only show delete for cohort-specific PDFs, admins only */}
                    {isStaff && !pdf.isMaster && (
                      <button 
                        className="btn-icon small danger"
                        onClick={() => handleDelete(pdf)}
                        title="Delete document"
                      >
                        🗑️
                      </button>
                    )}
                    {/* Show info icon for master PDFs */}
                    {isStaff && pdf.isMaster && (
                      <button 
                        className="btn-icon small"
                        onClick={() => setCurrentView('master-modules')}
                        title="Edit in Master Modules"
                      >
                        ⚙️
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="empty-state">
                <span className="empty-icon">📭</span>
                <p>No documents uploaded yet</p>
                {isStaff && (
                  <button className="btn-primary" onClick={() => setShowUploadModal(true)}>
                    Upload First Document
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {/* Info about document sources for admins */}
        {isStaff && allDocuments.length > 0 && (
          <div className="info-banner" style={{ marginTop: '16px' }}>
            <span>ℹ️</span>
            <span>
              Documents marked <strong>"All Cohorts"</strong> are master documents visible to every cohort. 
              Manage them in <a onClick={() => setCurrentView('master-modules')} style={{ color: '#2563eb', cursor: 'pointer' }}>Master Modules</a>.
            </span>
          </div>
        )}
      </div>

      {/* Course Summary */}
      <div className="syllabus-section">
        <div className="section-header">
          <h2>📋 Course Summary</h2>
          {isStaff && (
            <button 
              className="btn-secondary"
              onClick={() => setCurrentView('master-modules')}
            >
              Edit in Master Modules →
            </button>
          )}
        </div>
        
        <p className="section-description">
          All assignments and assessments for this course.
          {isStaff && ' To add or edit items, use the Master Modules section.'}
        </p>

        <div className="data-table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Week</th>
                <th>Assignment</th>
                <th>Type</th>
                <th>Due Day</th>
                <th>Points</th>
              </tr>
            </thead>
            <tbody>
              {courseSummary.length > 0 ? (
                courseSummary.map((item, index) => (
                  <tr 
                    key={`${item.id}-${index}`}
                    className="clickable"
                    onClick={() => handleAssignmentClick(item.weekNumber)}
                  >
                    <td>
                      <span className="week-badge">Week {item.weekNumber}</span>
                    </td>
                    <td>
                      <div className="assignment-cell">
                        <span>{getTypeIcon(item.type)}</span>
                        {item.title}
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${item.type}`}>
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </span>
                    </td>
                    <td>{item.dueDay || '-'}</td>
                    <td>{item.points || '-'}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="empty-cell">
                    No assignments scheduled yet
                    {isStaff && (
                      <button 
                        className="btn-link"
                        onClick={() => setCurrentView('master-modules')}
                      >
                        Add items in Master Modules
                      </button>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="total-points">
          <strong>Total Points:</strong> {courseSummary.reduce((sum, item) => sum + (item.points || 0), 0)}
        </div>
      </div>

      {/* Weekly Overview */}
      <div className="syllabus-section">
        <div className="section-header">
          <h2>📚 Weekly Overview</h2>
        </div>

        <div className="weekly-overview">
          {modules.map(module => {
            // Group items by day
            const daysOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
            const itemsByDay = {};
            
            (module.items || []).forEach(item => {
              const day = item.dueDay || item.due_day;
              if (day && daysOrder.includes(day)) {
                if (!itemsByDay[day]) itemsByDay[day] = [];
                itemsByDay[day].push(item);
              }
            });

            const daysWithItems = daysOrder.filter(day => itemsByDay[day]?.length > 0);
            const totalItems = (module.items || []).length;

            return (
              <div 
                key={module.weekNumber} 
                className="week-card"
                onClick={() => handleAssignmentClick(module.weekNumber)}
              >
                <div className="week-header">
                  <span className="week-number">Week {module.weekNumber}</span>
                  <h4>{module.title.replace(`Week ${module.weekNumber}: `, '')}</h4>
                </div>
                
                {daysWithItems.length > 0 ? (
                  <div className="week-days">
                    {daysWithItems.slice(0, 3).map(day => (
                      <div key={day} className="week-day-group">
                        <span className="week-day-label">📅 {day}</span>
                        <ul className="week-day-items">
                          {itemsByDay[day].slice(0, 2).map(item => (
                            <li key={item.id}>
                              <span className="item-icon">
                                {item.type === 'video' ? '🎬' : 
                                 item.type === 'assignment' ? '📝' : 
                                 item.type === 'quiz' ? '❓' :
                                 item.type === 'exam' ? '📋' : 
                                 item.type === 'start-here' ? '🚀' : '📖'}
                              </span>
                              {item.title}
                            </li>
                          ))}
                          {itemsByDay[day].length > 2 && (
                            <li className="more">+{itemsByDay[day].length - 2} more</li>
                          )}
                        </ul>
                      </div>
                    ))}
                    {daysWithItems.length > 3 && (
                      <div className="week-day-group more-days">
                        +{daysWithItems.length - 3} more days
                      </div>
                    )}
                  </div>
                ) : (
                  <ul className="week-items">
                    {(module.items || []).slice(0, 4).map(item => (
                      <li key={item.id}>
                        <span className="item-icon">
                          {item.type === 'video' ? '🎬' : 
                           item.type === 'assignment' ? '📝' : 
                           item.type === 'quiz' ? '❓' :
                           item.type === 'exam' ? '📋' : 
                           item.type === 'start-here' ? '🚀' : '📖'}
                        </span>
                        {item.title}
                      </li>
                    ))}
                    {totalItems > 4 && (
                      <li className="more">+{totalItems - 4} more items</li>
                    )}
                    {totalItems === 0 && (
                      <li className="empty">No items yet</li>
                    )}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Upload Modal - FOR COHORT-SPECIFIC PDFs */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Upload PDF Document</h3>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>×</button>
            </div>
            
            <form onSubmit={handleUpload}>
              <div className="form-group">
                <label>Document Title</label>
                <input 
                  type="text"
                  placeholder="e.g., Course Syllabus"
                  value={newPdf.title}
                  onChange={(e) => setNewPdf({...newPdf, title: e.target.value})}
                  required
                  disabled={uploading}
                />
              </div>
              
              <div className="form-group">
                <label>Upload PDF</label>
                <div className={`file-upload ${selectedFile ? 'has-file' : ''}`}>
                  <span className="upload-icon">📄</span>
                  <p>{selectedFile ? selectedFile.name : 'Drag and drop or click to upload'}</p>
                  <input 
                    type="file" 
                    accept=".pdf,application/pdf"
                    onChange={handleFileSelect}
                    disabled={uploading}
                  />
                </div>
                {selectedFile && (
                  <p className="selected-file">
                    ✓ Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </p>
                )}
              </div>

              {/* Info about cohort-specific upload */}
              <div className="info-banner" style={{ marginTop: '8px', marginBottom: '16px' }}>
                <span>ℹ️</span>
                <span>
                  This document will only be visible to <strong>{cohort.name}</strong>. 
                  To add documents for all cohorts, use <a onClick={() => { setShowUploadModal(false); setCurrentView('master-modules'); }} style={{ color: '#2563eb', cursor: 'pointer' }}>Master Modules</a>.
                </span>
              </div>
              
              {uploadError && (
                <div className="form-error">
                  ❌ {uploadError}
                </div>
              )}
              
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn-secondary" 
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFile(null);
                    setUploadError('');
                  }}
                  disabled={uploading}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={uploading || !selectedFile}>
                  {uploading ? '⏳ Uploading...' : 'Upload Document'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SyllabusView;

